
import { useState, useEffect, useRef } from 'react';

export default function InteractiveCalculator() {
  const [calcJobType, setCalcJobType] = useState('hvac');
  const [calcHours, setCalcHours] = useState(8);
  const [calcMaterials, setCalcMaterials] = useState(1500);
  const [calcArea, setCalcArea] = useState('melbourne-cbd');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Observe visibility of the component for entrance animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Area rates (rate per hour before job‑type multiplier)
  const areaRates: Record<
    string,
    { name: string; rate: number }
  > = {
    'melbourne-cbd': { name: 'Melbourne CBD', rate: 174 },
    'inner-suburbs': { name: 'Inner Suburbs', rate: 158 },
    'outer-suburbs': { name: 'Outer Suburbs', rate: 142 },
    regional: { name: 'Regional VIC', rate: 125 },
  };

  // Job‑type definitions
  const jobTypes: Record<
    string,
    { name: string; icon: string; multiplier: number }
  > = {
    hvac: { name: 'HVAC', icon: 'ri-temp-cold-line', multiplier: 1.2 },
    plumbing: { name: 'Plumbing', icon: 'ri-drop-line', multiplier: 1.0 },
    electrical: {
      name: 'Electrical',
      icon: 'ri-flashlight-line',
      multiplier: 1.15,
    },
    carpentry: { name: 'Carpentry', icon: 'ri-hammer-line', multiplier: 0.95 },
    roofing: { name: 'Roofing', icon: 'ri-home-gear-line', multiplier: 1.3 },
    painting: { name: 'Painting', icon: 'ri-paint-brush-line', multiplier: 0.9 },
  };

  // Defensive calculations – guard against undefined keys
  const selectedArea = areaRates[calcArea];
  const selectedJob = jobTypes[calcJobType];

  if (!selectedArea || !selectedJob) {
    // If something goes wrong, render a friendly fallback instead of crashing
    return (
      <section className="py-28 bg-slate-900 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-red-400">
            An unexpected configuration was detected. Please reload the page.
          </p>
        </div>
      </section>
    );
  }

  const marketRate = selectedArea.rate * selectedJob.multiplier;
  const marketLabor = marketRate * calcHours;
  const marketTotal = marketLabor + calcMaterials;

  return (
    <section
      ref={sectionRef}
      className="py-28 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950 relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-amber-500/[0.03] rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/15 mb-6">
            <i className="ri-calculator-line text-amber-400 text-sm" />
            <span className="text-amber-400 font-medium text-sm">
              Try It Yourself
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Interactive{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              Pricing Calculator
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Adjust the controls to see how our AI calculates optimal pricing
            for any construction job
          </p>
        </div>

        <div
          className={`grid lg:grid-cols-2 gap-8 items-start transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Controls */}
          <div className="bg-white/[0.04] backdrop-blur-xl rounded-3xl border border-white/[0.08] p-8">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-settings-3-line text-amber-400" />
              Configure Your Job
            </h3>

            {/* Job Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Job Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(jobTypes).map(([key, job]) => (
                  <button
                    key={key}
                    onClick={() => setCalcJobType(key)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all cursor-pointer ${
                      calcJobType === key
                        ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                        : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        calcJobType === key
                          ? 'bg-amber-500/30'
                          : 'bg-white/10'
                      }`}
                    >
                      <i className={`${job.icon} text-lg`} />
                    </div>
                    <span className="text-xs font-medium">{job.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Service Area
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(areaRates).map(([key, area]) => (
                  <button
                    key={key}
                    onClick={() => setCalcArea(key)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      calcArea === key
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                        : 'bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {area.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Hours */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-300">
                  Estimated Hours
                </label>
                <span className="text-amber-400 font-semibold">
                  {calcHours} hrs
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="40"
                value={calcHours}
                onChange={e => setCalcHours(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-r [&::-webkit-slider-thumb]:from-amber-500 [&::-webkit-slider-thumb]:to-orange-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-amber-500/30"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>1 hr</span>
                <span>40 hrs</span>
              </div>
            </div>

            {/* Materials */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-300">
                  Material Costs
                </label>
                <span className="text-amber-400 font-semibold">
                  ${calcMaterials.toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="10000"
                step="100"
                value={calcMaterials}
                onChange={e => setCalcMaterials(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-r [&::-webkit-slider-thumb]:from-amber-500 [&::-webkit-slider-thumb]:to-orange-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-amber-500/30"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>$0</span>
                <span>$10,000</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 backdrop-blur-xl rounded-3xl border border-amber-500/30 p-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-slate-400 text-sm mb-1">
                    Market Rate Total
                  </p>
                  <p className="text-5xl font-bold text-white">
                    ${marketTotal.toLocaleString()}
                  </p>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-amber-500/30 flex items-center justify-center">
                  <i className="ri-trophy-line text-amber-400 text-3xl" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-slate-400 text-sm">Hourly Rate</span>
                  <span className="text-white font-semibold">
                    ${marketRate.toFixed(0)}/hr
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-slate-400 text-sm">
                    Labor ({calcHours} hrs)
                  </span>
                  <span className="text-white font-semibold">
                    ${marketLabor.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-slate-400 text-sm">Materials</span>
                  <span className="text-white font-semibold">
                    ${calcMaterials.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-slate-400 text-sm">Area Rate</span>
                  <span className="text-amber-400 font-semibold flex items-center gap-1">
                    <i className="ri-arrow-up-line" /> Top 10%
                  </span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white/5 rounded-xl">
                <p className="text-[10px] text-slate-400 mb-1 uppercase tracking-wider font-medium">
                  AI Recommendation
                </p>
                <p className="text-sm text-white">
                  Premium pricing for {selectedJob.name} in{' '}
                  {selectedArea.name}. Reflects top-tier expertise and quality
                  materials.
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  label: 'Profit Margin',
                  value: `${Math.round((marketLabor / marketTotal) * 100)}%`,
                  icon: 'ri-line-chart-line',
                },
                { label: 'vs Average', value: '+18%', icon: 'ri-arrow-up-circle-line' },
                { label: 'Win Rate', value: '87%', icon: 'ri-trophy-line' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4 text-center"
                >
                  <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} text-amber-400 text-sm`} />
                  </div>
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
